import { useState } from "react";
import {
  useCreate,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { HELP_CLASS, SectionCard } from "../guardias/guardiasShared";
import type { WhatsAppTemplateRow } from "../types";
import {
  parseVariables,
  validateTemplateForm,
  type TemplateField,
  type TemplateFormValues,
} from "./whatsappSettingsRules";

const EMPTY_FORM: TemplateFormValues = {
  name: "",
  language: "es_MX",
  label: "",
  variables: "",
  isPaid: true,
};

const toForm = (row: WhatsAppTemplateRow): TemplateFormValues => ({
  name: row.name,
  language: row.language,
  label: row.label,
  variables: row.variables.join(", "),
  isPaid: row.is_paid,
});

const TemplateForm = ({
  initial,
  submitLabel,
  isPending,
  onSubmit,
  onCancel,
}: {
  initial: TemplateFormValues;
  submitLabel: string;
  isPending: boolean;
  onSubmit: (values: TemplateFormValues) => void;
  onCancel?: () => void;
}) => {
  const translate = useTranslate();
  const [values, setValues] = useState(initial);
  const [invalid, setInvalid] = useState<TemplateField[]>([]);
  const set = <K extends keyof TemplateFormValues>(
    key: K,
    value: TemplateFormValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const submit = () => {
    const fields = validateTemplateForm(values);
    setInvalid(fields);
    if (fields.length === 0) onSubmit(values);
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="whatsapp-template-label">
            {translate("crm.whatsapp.settings.template_label")}
          </Label>
          <Input
            id="whatsapp-template-label"
            className="h-11 text-base"
            value={values.label}
            aria-invalid={invalid.includes("label")}
            onChange={(event) => set("label", event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="whatsapp-template-name">
            {translate("crm.whatsapp.settings.template_name")}
          </Label>
          <Input
            id="whatsapp-template-name"
            className="h-11 text-base"
            value={values.name}
            aria-invalid={invalid.includes("name")}
            onChange={(event) => set("name", event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="whatsapp-template-language">
            {translate("crm.whatsapp.settings.template_language")}
          </Label>
          <Input
            id="whatsapp-template-language"
            className="h-11 text-base"
            value={values.language}
            aria-invalid={invalid.includes("language")}
            onChange={(event) => set("language", event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="whatsapp-template-variables">
            {translate("crm.whatsapp.settings.template_variables")}
          </Label>
          <Input
            id="whatsapp-template-variables"
            className="h-11 text-base"
            value={values.variables}
            onChange={(event) => set("variables", event.target.value)}
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-base">
        <input
          type="checkbox"
          className="h-5 w-5"
          checked={values.isPaid}
          onChange={(event) => set("isPaid", event.target.checked)}
        />
        {translate("crm.whatsapp.settings.template_paid")}
      </label>
      {invalid.length > 0 ? (
        <p role="alert" className="text-[15px] text-destructive">
          {translate("crm.whatsapp.settings.template_invalid")}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          className="h-11"
          disabled={isPending}
          onClick={submit}
        >
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={onCancel}
          >
            {translate("crm.whatsapp.inbox.cancel")}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const TemplateRow = ({ row }: { row: WhatsAppTemplateRow }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [update, { isPending }] = useUpdate();
  const [editing, setEditing] = useState(false);

  const save = (data: Partial<WhatsAppTemplateRow>, done?: () => void) =>
    update(
      "whatsapp_templates",
      {
        id: row.id,
        data: { ...data, updated_by: identity?.id ?? null },
        previousData: row,
      },
      {
        onSuccess: () => {
          notify("crm.whatsapp.settings.saved", { type: "success" });
          done?.();
        },
        onError: () =>
          notify("crm.whatsapp.settings.save_error", { type: "error" }),
      },
    );

  if (editing) {
    return (
      <TemplateForm
        initial={toForm(row)}
        submitLabel={translate("crm.whatsapp.settings.save")}
        isPending={isPending}
        onCancel={() => setEditing(false)}
        onSubmit={(values) =>
          save(
            {
              name: values.name.trim(),
              language: values.language.trim(),
              label: values.label.trim(),
              variables: parseVariables(values.variables),
              is_paid: values.isPaid,
            },
            () => setEditing(false),
          )
        }
      />
    );
  }

  return (
    <div
      data-testid="whatsapp-template-row"
      className="flex flex-wrap items-center gap-2 rounded-md border p-3"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-semibold">{row.label}</p>
        <p className={HELP_CLASS}>
          {row.name} · {row.language} ·{" "}
          {translate(
            row.is_paid
              ? "crm.whatsapp.settings.paid"
              : "crm.whatsapp.settings.free",
          )}
          {row.is_active
            ? ""
            : ` · ${translate("crm.whatsapp.settings.archived")}`}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-11"
          onClick={() => setEditing(true)}
        >
          {translate("crm.whatsapp.settings.rename")}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11"
          disabled={isPending}
          onClick={() => save({ is_paid: !row.is_paid })}
        >
          {translate(
            row.is_paid
              ? "crm.whatsapp.settings.mark_free"
              : "crm.whatsapp.settings.mark_paid",
          )}
        </Button>
        {/* Archive, never delete: the row stays for the audit trail. */}
        <Button
          type="button"
          variant="outline"
          className="h-11"
          disabled={isPending}
          onClick={() => save({ is_active: !row.is_active })}
        >
          {translate(
            row.is_active
              ? "crm.whatsapp.settings.archive"
              : "crm.whatsapp.settings.restore",
          )}
        </Button>
      </div>
    </div>
  );
};

const TemplateAdd = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [create, { isPending }] = useCreate();
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        className="h-11"
        onClick={() => setOpen(true)}
      >
        {translate("crm.whatsapp.settings.template_add")}
      </Button>
    );
  }
  return (
    <TemplateForm
      initial={EMPTY_FORM}
      submitLabel={translate("crm.whatsapp.settings.template_add_confirm")}
      isPending={isPending}
      onCancel={() => setOpen(false)}
      onSubmit={(values) =>
        create(
          "whatsapp_templates",
          {
            data: {
              name: values.name.trim(),
              language: values.language.trim(),
              label: values.label.trim(),
              variables: parseVariables(values.variables),
              is_paid: values.isPaid,
              is_active: true,
              updated_by: identity?.id ?? null,
            },
          },
          {
            onSuccess: () => {
              notify("crm.whatsapp.settings.saved", { type: "success" });
              setOpen(false);
            },
            onError: () =>
              notify("crm.whatsapp.settings.save_error", { type: "error" }),
          },
        )
      }
    />
  );
};

/**
 * Almikia Epic EW2 (TASK-080): the approved-template catalogue (`whatsapp_templates`): add,
 * rename, mark paid or free, archive and restore. Never delete: there is no delete control and
 * the table has no delete policy. Direccion only (page gate and RLS).
 */
export const WhatsAppTemplatesCard = () => {
  const translate = useTranslate();
  const templates = useGetList<WhatsAppTemplateRow>("whatsapp_templates", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "label", order: "ASC" },
  });
  return (
    <SectionCard
      title={translate("crm.whatsapp.settings.templates_title")}
      help={translate("crm.whatsapp.settings.templates_help")}
    >
      <TemplateAdd />
      {templates.error ? (
        <ListErrorState
          message={translate("crm.whatsapp.settings.load_error")}
          onRetry={() => templates.refetch()}
        />
      ) : !templates.data ? (
        <ListCardsSkeleton rows={2} />
      ) : templates.data.length === 0 ? (
        <ListEmptyState
          title={translate("crm.whatsapp.settings.templates_empty")}
        />
      ) : (
        <div className="space-y-2">
          {templates.data.map((row) => (
            <TemplateRow key={String(row.id)} row={row} />
          ))}
        </div>
      )}
    </SectionCard>
  );
};
